import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X } from 'lucide-react';

const PopupDisplay = ({ pageUrl }) => {
  const [popups, setPopups] = useState([]);
  const [activePopup, setActivePopup] = useState(null);
  const [showCloseButton, setShowCloseButton] = useState(true);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 
    (window.location.origin.includes("localhost") 
      ? "http://localhost:8000" 
      : window.location.origin + "/pandvah-backend");

  useEffect(() => {
    fetchActivePopups();
  }, [pageUrl]);

  useEffect(() => {
    if (popups.length > 0) {
      checkTriggers();
    }
  }, [popups]);

  const fetchActivePopups = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/popups/active/for-page?page_url=${encodeURIComponent(pageUrl)}`
      );
      
      if (response.data.success) {
        // Filter out popups that have been shown before (check localStorage)
        const unseenPopups = response.data.popups.filter(popup => {
          const shown = localStorage.getItem(`popup_shown_${popup.id}`);
          return !shown;
        });
        setPopups(unseenPopups);
      }
    } catch (error) {
      console.error('Error fetching popups:', error);
    }
  };

  const checkTriggers = () => {
    popups.forEach(popup => {
      const trigger = popup.trigger;
      
      switch (trigger.type) {
        case 'immediate':
          showPopup(popup);
          break;
          
        case 'timer':
          setTimeout(() => showPopup(popup), trigger.value * 1000);
          break;
          
        case 'inactivity':
          setupInactivityTrigger(popup, trigger.value);
          break;
          
        case 'scroll':
          setupScrollTrigger(popup, trigger.value);
          break;
          
        case 'exit_intent':
          setupExitIntentTrigger(popup);
          break;
          
        default:
          break;
      }
    });
  };

  const setupInactivityTrigger = (popup, seconds) => {
    let inactivityTimer;
    
    const resetTimer = () => {
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(() => showPopup(popup), seconds * 1000);
    };
    
    document.addEventListener('mousemove', resetTimer);
    document.addEventListener('keypress', resetTimer);
    resetTimer();
  };

  const setupScrollTrigger = (popup, percentage) => {
    const handleScroll = () => {
      const scrollPercent = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
      if (scrollPercent >= percentage) {
        showPopup(popup);
        window.removeEventListener('scroll', handleScroll);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
  };

  const setupExitIntentTrigger = (popup) => {
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0) {
        showPopup(popup);
        document.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
    
    document.addEventListener('mouseleave', handleMouseLeave);
  };

  const showPopup = (popup) => {
    if (activePopup) return; // Don't show if another popup is active
    
    setActivePopup(popup);
    setSubmitted(false);
    setFormData({});
    
    // Handle close button visibility
    if (popup.configuration.close_button_behavior === 'after_delay') {
      setShowCloseButton(false);
      setTimeout(() => setShowCloseButton(true), popup.configuration.close_button_delay * 1000);
    } else {
      setShowCloseButton(popup.configuration.close_button_behavior !== 'none');
    }
    
    // Mark as shown in localStorage
    localStorage.setItem(`popup_shown_${popup.id}`, 'true');
  };

  const closePopup = () => {
    setActivePopup(null);
    setSubmitted(false);
  };

  const handleInputChange = (fieldLabel, value) => {
    setFormData({ ...formData, [fieldLabel]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const requiredFields = activePopup.configuration.form_fields.filter(f => f.required);
    const missingFields = requiredFields.filter(f => !formData[f.label]);
    
    if (missingFields.length > 0) {
      alert(`Please fill in all required fields: ${missingFields.map(f => f.label).join(', ')}`);
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE_URL}/api/popups/${activePopup.id}/submit`, {
        popup_id: activePopup.id,
        form_data: formData,
        page_url: pageUrl
      });
      
      setSubmitted(true);
      setTimeout(() => closePopup(), 2000);
    } catch (error) {
      console.error('Error submitting popup form:', error);
      alert('Failed to submit form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!activePopup) return null;

  const { configuration } = activePopup;
  
  // Determine layout - default to 'left' if image exists but no layout specified
  const layout = configuration.layout || 
    (configuration.image_url ? 'left' : 'text_only');

  // Size classes - adjust based on layout and image format
  // For square images (800x800), use wider modals
  // For portrait images (600x900), use standard widths
  const imageFormat = configuration.image_format || 'portrait';
  const isSquareImage = imageFormat === 'square';
  
  const sizeClasses = (layout === 'left' || layout === 'right') ? {
    small: isSquareImage ? 'max-w-3xl' : 'max-w-2xl',
    medium: isSquareImage ? 'max-w-4xl' : 'max-w-3xl',
    large: isSquareImage ? 'max-w-6xl' : 'max-w-5xl',
    fullscreen: 'max-w-full h-full'
  } : {
    small: 'max-w-md',
    medium: 'max-w-lg',
    large: 'max-w-2xl',
    fullscreen: 'max-w-full h-full'
  };

  const positionClasses = {
    center: 'items-center justify-center',
    'bottom-right': 'items-end justify-end p-6',
    'top-banner': 'items-start justify-center pt-0'
  };

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 z-50 flex ${positionClasses[configuration.position] || 'items-center justify-center'}`}
      style={{ zIndex: 9999 }}
    >
      {/* Background Layout */}
      {layout === 'background' && (
        <div
          className={`bg-white rounded-lg shadow-2xl ${sizeClasses[configuration.size]} w-full mx-4 relative overflow-hidden`}
        >
          {/* Background Image */}
          {configuration.image_url && (
            <div 
              className="absolute inset-0"
              style={{
                backgroundImage: `url(${configuration.image_url})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                opacity: (configuration.image_opacity || 100) / 100
              }}
            />
          )}
          
          {/* Close Button */}
          {showCloseButton && (
            <button
              onClick={closePopup}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
            >
              <X className="h-6 w-6" />
            </button>
          )}

          <div className="p-6 relative z-10">
            {submitted ? (
              <div className="text-center py-12">
                <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Thank You!</h3>
                <p className="text-gray-600">Your submission has been received.</p>
              </div>
            ) : (
              <>
                <h2 className="text-lg font-bold mb-3" style={{ color: configuration.text_color }}>
                  {configuration.title}
                </h2>
                {configuration.description && (
                  <p className="text-sm text-gray-600 mb-6" style={{ color: configuration.text_color }}>
                    {configuration.description}
                  </p>
                )}
                <form onSubmit={handleSubmit} className="space-y-4">
                  {configuration.form_fields
                    .sort((a, b) => a.order - b.order)
                    .map((field, index) => (
                      <div key={index}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {field.label}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        {field.field_type === 'textarea' ? (
                          <textarea
                            value={formData[field.label] || ''}
                            onChange={(e) => handleInputChange(field.label, e.target.value)}
                            placeholder={field.placeholder}
                            required={field.required}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            rows="3"
                          />
                        ) : field.field_type === 'checkbox' ? (
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={formData[field.label] || false}
                              onChange={(e) => handleInputChange(field.label, e.target.checked)}
                              required={field.required}
                              className="mr-2"
                            />
                            <span className="text-sm text-gray-700">{field.placeholder}</span>
                          </label>
                        ) : (
                          <input
                            type={field.field_type}
                            value={formData[field.label] || ''}
                            onChange={(e) => handleInputChange(field.label, e.target.value)}
                            placeholder={field.placeholder}
                            required={field.required}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        )}
                      </div>
                    ))}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3 px-4 rounded-md text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                    style={{ backgroundColor: configuration.button_color }}
                  >
                    {submitting ? 'Submitting...' : configuration.button_text}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Left Layout */}
      {layout === 'left' && (
        <div
          className={`bg-white rounded-lg shadow-2xl ${sizeClasses[configuration.size]} w-full mx-4 relative overflow-hidden flex`}
          style={{ backgroundColor: configuration.background_color }}
        >
          {/* Close Button */}
          {showCloseButton && (
            <button
              onClick={closePopup}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
            >
              <X className="h-6 w-6" />
            </button>
          )}

          {/* Image on Left - Hidden on mobile, shown on md+ screens */}
          {configuration.image_url && (
            <div className={`hidden md:block flex-shrink-0 ${configuration.image_format === 'square' ? 'md:w-1/2' : 'md:w-2/5'}`}>
              <img
                src={configuration.image_url}
                alt="Popup"
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Form Content on Right */}
          <div className="flex-1 p-6">
            {submitted ? (
              <div className="text-center py-12">
                <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Thank You!</h3>
                <p className="text-gray-600">Your submission has been received.</p>
              </div>
            ) : (
              <>
                <h2 className="text-lg font-bold mb-3" style={{ color: configuration.text_color }}>
                  {configuration.title}
                </h2>
                {configuration.description && (
                  <p className="text-sm text-gray-600 mb-6" style={{ color: configuration.text_color }}>
                    {configuration.description}
                  </p>
                )}
                <form onSubmit={handleSubmit} className="space-y-4">
                  {configuration.form_fields
                    .sort((a, b) => a.order - b.order)
                    .map((field, index) => (
                      <div key={index}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {field.label}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        {field.field_type === 'textarea' ? (
                          <textarea
                            value={formData[field.label] || ''}
                            onChange={(e) => handleInputChange(field.label, e.target.value)}
                            placeholder={field.placeholder}
                            required={field.required}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            rows="3"
                          />
                        ) : field.field_type === 'checkbox' ? (
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={formData[field.label] || false}
                              onChange={(e) => handleInputChange(field.label, e.target.checked)}
                              required={field.required}
                              className="mr-2"
                            />
                            <span className="text-sm text-gray-700">{field.placeholder}</span>
                          </label>
                        ) : (
                          <input
                            type={field.field_type}
                            value={formData[field.label] || ''}
                            onChange={(e) => handleInputChange(field.label, e.target.value)}
                            placeholder={field.placeholder}
                            required={field.required}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        )}
                      </div>
                    ))}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3 px-4 rounded-md text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                    style={{ backgroundColor: configuration.button_color }}
                  >
                    {submitting ? 'Submitting...' : configuration.button_text}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Right Layout */}
      {layout === 'right' && (
        <div
          className={`bg-white rounded-lg shadow-2xl ${sizeClasses[configuration.size]} w-full mx-4 relative overflow-hidden flex`}
          style={{ backgroundColor: configuration.background_color }}
        >
          {/* Close Button */}
          {showCloseButton && (
            <button
              onClick={closePopup}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
            >
              <X className="h-6 w-6" />
            </button>
          )}

          {/* Form Content on Left */}
          <div className="flex-1 p-6">
            {submitted ? (
              <div className="text-center py-12">
                <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Thank You!</h3>
                <p className="text-gray-600">Your submission has been received.</p>
              </div>
            ) : (
              <>
                <h2 className="text-lg font-bold mb-3" style={{ color: configuration.text_color }}>
                  {configuration.title}
                </h2>
                {configuration.description && (
                  <p className="text-sm text-gray-600 mb-6" style={{ color: configuration.text_color }}>
                    {configuration.description}
                  </p>
                )}
                <form onSubmit={handleSubmit} className="space-y-4">
                  {configuration.form_fields
                    .sort((a, b) => a.order - b.order)
                    .map((field, index) => (
                      <div key={index}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {field.label}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        {field.field_type === 'textarea' ? (
                          <textarea
                            value={formData[field.label] || ''}
                            onChange={(e) => handleInputChange(field.label, e.target.value)}
                            placeholder={field.placeholder}
                            required={field.required}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            rows="3"
                          />
                        ) : field.field_type === 'checkbox' ? (
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={formData[field.label] || false}
                              onChange={(e) => handleInputChange(field.label, e.target.checked)}
                              required={field.required}
                              className="mr-2"
                            />
                            <span className="text-sm text-gray-700">{field.placeholder}</span>
                          </label>
                        ) : (
                          <input
                            type={field.field_type}
                            value={formData[field.label] || ''}
                            onChange={(e) => handleInputChange(field.label, e.target.value)}
                            placeholder={field.placeholder}
                            required={field.required}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        )}
                      </div>
                    ))}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3 px-4 rounded-md text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                    style={{ backgroundColor: configuration.button_color }}
                  >
                    {submitting ? 'Submitting...' : configuration.button_text}
                  </button>
                </form>
              </>
            )}
          </div>

          {/* Image on Right - Hidden on mobile, shown on md+ screens */}
          {configuration.image_url && (
            <div className={`hidden md:block flex-shrink-0 ${configuration.image_format === 'square' ? 'md:w-1/2' : 'md:w-2/5'}`}>
              <img
                src={configuration.image_url}
                alt="Popup"
                className="w-full h-full object-cover"
              />
            </div>
          )}
        </div>
      )}

      {/* Text Only Layout (default) */}
      {layout === 'text_only' && (
        <div
          className={`bg-white rounded-lg shadow-2xl ${sizeClasses[configuration.size]} w-full mx-4 relative`}
          style={{ backgroundColor: configuration.background_color }}
        >
          {/* Close Button */}
          {showCloseButton && (
            <button
              onClick={closePopup}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
            >
              <X className="h-6 w-6" />
            </button>
          )}

          <div className="p-6">
            {submitted ? (
              <div className="text-center py-12">
                <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Thank You!</h3>
                <p className="text-gray-600">Your submission has been received.</p>
              </div>
            ) : (
              <>
                {/* Image (if any in text_only mode for backwards compatibility) */}
                {configuration.image_url && (
                  <div className="mb-6">
                    <img
                      src={configuration.image_url}
                      alt="Popup"
                      className="w-full h-48 object-cover rounded-lg"
                    />
                  </div>
                )}

                <h2 className="text-lg font-bold mb-3" style={{ color: configuration.text_color }}>
                  {configuration.title}
                </h2>
                {configuration.description && (
                  <p className="text-sm text-gray-600 mb-6" style={{ color: configuration.text_color }}>
                    {configuration.description}
                  </p>
                )}
                <form onSubmit={handleSubmit} className="space-y-4">
                  {configuration.form_fields
                    .sort((a, b) => a.order - b.order)
                    .map((field, index) => (
                      <div key={index}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {field.label}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        {field.field_type === 'textarea' ? (
                          <textarea
                            value={formData[field.label] || ''}
                            onChange={(e) => handleInputChange(field.label, e.target.value)}
                            placeholder={field.placeholder}
                            required={field.required}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            rows="3"
                          />
                        ) : field.field_type === 'checkbox' ? (
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={formData[field.label] || false}
                              onChange={(e) => handleInputChange(field.label, e.target.checked)}
                              required={field.required}
                              className="mr-2"
                            />
                            <span className="text-sm text-gray-700">{field.placeholder}</span>
                          </label>
                        ) : (
                          <input
                            type={field.field_type}
                            value={formData[field.label] || ''}
                            onChange={(e) => handleInputChange(field.label, e.target.value)}
                            placeholder={field.placeholder}
                            required={field.required}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        )}
                      </div>
                    ))}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3 px-4 rounded-md text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                    style={{ backgroundColor: configuration.button_color }}
                  >
                    {submitting ? 'Submitting...' : configuration.button_text}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PopupDisplay;
